import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";

const KIND_OPTIONS = [
  "wall_rack", "gondola", "end_cap", "corner_rack",
  "slotted_angle", "garment", "display_table",
  "billing_counter", "storage_counter", "custom",
];

const UNIT_TYPE_OPTIONS = [
  { v: "main",   l: "Main (2 legs)" },
  { v: "addon",  l: "Add-on (1 leg, shared)" },
  { v: "corner", l: "Corner (3 legs, L-shape)" },
  { v: "centre", l: "Centre (2-sided gondola)" },
];

const DEFAULT_LEGS = { main: 2, addon: 1, corner: 3, centre: 2 };

const blank = {
  name: "New rack",
  kind: "custom",
  unit_type: "main",
  leg_count: 2,
  default_length_ft: 4, default_depth_ft: 2, default_height_ft: 7,
  min_length_ft: 2, max_length_ft: 12,
  min_depth_ft: 1, max_depth_ft: 4,
  pricing_mode: "unit",
  unit_price: 5000, price_per_ft: 0,
  weight_kg: 50, color: "#0ea5e9", wall_attached: false, notes: "",
};

export default function RackLibrary() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/floorplan/library").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/floorplan/library/${editing.id}`, editing);
      else await api.post("/floorplan/library", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const del = async (it) => { if (!window.confirm(`Delete "${it.name}"?`)) return; await api.delete(`/floorplan/library/${it.id}`); toast.success("Deleted"); load(); };

  return (
    <Layout>
      <PageHeader eyebrow="Admin · Floor Plan" title="Rack Library"
        subtitle="Items available in the manual layout builder. Edit sizes, pricing and colours."
        actions={<button onClick={() => setEditing({ ...blank })} className="btn-primary" data-testid="rl-new"><Plus size={16}/> Add item</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 panel">
          <table className="w-full grid-lines text-xs">
            <thead><tr><th></th><th>Name</th><th>Kind</th><th>Unit Type</th><th className="num">Legs</th><th>Default size</th><th>Pricing</th><th className="num">Weight</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className={editing?.id === it.id ? "bg-orange-50" : ""} data-testid={`rl-row-${it.id}`}>
                  <td><span style={{ width: 14, height: 14, background: it.color, display: "inline-block", border: "1px solid #0f172a" }}/></td>
                  <td className="font-bold">{it.name}</td>
                  <td className="mono">{it.kind}</td>
                  <td className="mono uppercase">{it.unit_type || "main"}</td>
                  <td className="num mono">{it.leg_count ?? DEFAULT_LEGS[it.unit_type || "main"]}</td>
                  <td className="mono">{it.default_length_ft}×{it.default_depth_ft}×{it.default_height_ft} ft</td>
                  <td className="mono">{it.pricing_mode === "per_ft" ? `₹${it.price_per_ft}/ft` : `₹${it.unit_price}/unit`}</td>
                  <td className="num mono">{it.weight_kg} kg</td>
                  <td className="flex gap-1.5 py-2">
                    <button onClick={() => setEditing({ ...it })} className="btn-ghost text-[0.65rem] py-0.5">Edit</button>
                    <button onClick={() => del(it)} className="btn-danger text-[0.65rem] py-0.5"><Trash2 size={10}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editing && (
          <div className="panel p-5" data-testid="rl-editor">
            <div className="label-tech mb-3">{editing.id ? "Edit item" : "New item"}</div>
            <div className="space-y-3 text-xs">
              <Field label="Name"><input className="input-tech mono text-xs" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}/></Field>
              <Field label="Kind">
                <select className="input-tech mono text-xs" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Unit Type (auto-layout)">
                  <select className="input-tech mono text-xs"
                          value={editing.unit_type || "main"}
                          onChange={(e) => setEditing({ ...editing, unit_type: e.target.value, leg_count: DEFAULT_LEGS[e.target.value] })}>
                    {UNIT_TYPE_OPTIONS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </Field>
                <Field label="Legs per unit">
                  <input type="number" min="1" max="8" className="input-tech mono text-xs"
                         value={editing.leg_count ?? DEFAULT_LEGS[editing.unit_type || "main"]}
                         onChange={(e) => setEditing({ ...editing, leg_count: Number(e.target.value) })}/>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="L ft"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.default_length_ft} onChange={(e) => setEditing({ ...editing, default_length_ft: Number(e.target.value) })}/></Field>
                <Field label="D ft"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.default_depth_ft} onChange={(e) => setEditing({ ...editing, default_depth_ft: Number(e.target.value) })}/></Field>
                <Field label="H ft"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.default_height_ft} onChange={(e) => setEditing({ ...editing, default_height_ft: Number(e.target.value) })}/></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Min L"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.min_length_ft} onChange={(e) => setEditing({ ...editing, min_length_ft: Number(e.target.value) })}/></Field>
                <Field label="Max L"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.max_length_ft} onChange={(e) => setEditing({ ...editing, max_length_ft: Number(e.target.value) })}/></Field>
                <Field label="Min D"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.min_depth_ft} onChange={(e) => setEditing({ ...editing, min_depth_ft: Number(e.target.value) })}/></Field>
                <Field label="Max D"><input type="number" step="0.5" className="input-tech mono text-xs" value={editing.max_depth_ft} onChange={(e) => setEditing({ ...editing, max_depth_ft: Number(e.target.value) })}/></Field>
              </div>
              <Field label="Pricing mode">
                <select className="input-tech mono text-xs" value={editing.pricing_mode} onChange={(e) => setEditing({ ...editing, pricing_mode: e.target.value })}>
                  <option value="unit">Per unit</option>
                  <option value="per_ft">Per running foot</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="₹/unit"><input type="number" className="input-tech mono text-xs" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: Number(e.target.value) })}/></Field>
                <Field label="₹/ft"><input type="number" className="input-tech mono text-xs" value={editing.price_per_ft} onChange={(e) => setEditing({ ...editing, price_per_ft: Number(e.target.value) })}/></Field>
              </div>
              <Field label="Weight (kg / unit or /ft)"><input type="number" className="input-tech mono text-xs" value={editing.weight_kg} onChange={(e) => setEditing({ ...editing, weight_kg: Number(e.target.value) })}/></Field>
              <Field label="Color">
                <input type="color" className="input-tech text-xs h-8 p-1" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })}/>
              </Field>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={editing.wall_attached} onChange={(e) => setEditing({ ...editing, wall_attached: e.target.checked })}/> Wall-attached by default</label>
              <Field label="Notes"><textarea className="input-tech text-xs h-16 py-1.5" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}/></Field>
              <div className="flex gap-2 pt-3">
                <button onClick={save} className="btn-primary flex-1"><Save size={14}/> Save</button>
                <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tech text-[0.6rem] mb-1 block">{label}</label>
      {children}
    </div>
  );
}
